import { createSelector } from 'reselect'

import moment from 'moment-timezone'

import { selectCompletedAgents, selectPendingAgents, selectSuggestedAgents } from './account'

const selectManageUsers = state => state.manage_users || {}
export const selectIsSendingAgentInvites = createSelector(selectManageUsers, state => state.sendingAgentInvites || false)
export const selectIsSendingAdminInvites = createSelector(selectManageUsers, state => state.sendingAdminInvites || false)

//////////////////////////////////////
// AGENT INVITES
//////////////////////////////////////
export const selectAgentInvites = createSelector(selectManageUsers, state => state.agentInvites || [])

// TODO (Jared): Come up with a function for pulling the name based on the fields that are actually populated
const searchAgentToRedux = (agent, idx) => {
  const padLength = 4
  return {
    ...agent,
    email: agent.agent_email,
    name: agent.agent_best_name,
    party_id: agent.agent_party_id,
    key: `${moment().format('x')}:${_.padStart(idx.toString(), padLength, '0')}`
  }
}

const selectInvitedAgentEmails = createSelector(selectCompletedAgents, selectPendingAgents, (completed, pending) => {
  const invitedAgents = _.concat(completed, pending)
  return _.map(invitedAgents, ia => ia.email)
})

export const selectSelectedSearchResults = createSelector(selectManageUsers, state => state.selectedSearchResults || [])

const selectRemovedSearchResults = createSelector(selectManageUsers, state => state.removedSearchResults || [])

const selectRawSearchResults = createSelector(selectSuggestedAgents, agents => agents)

const selectFilteredSearchResults = createSelector(selectRawSearchResults, selectInvitedAgentEmails, (results, invited) =>
  _.filter(results, result => !_.isNil(result.agent_email) && !result.agent_op_id && !_.includes(invited, result.agent_email)))

export const selectSanitizedSearchResults = createSelector(selectFilteredSearchResults, results =>
  _.map(_.sortBy(results, ['recommended', 'priority_score']), searchAgentToRedux))

export const selectSearchResults = createSelector(selectAgentInvites, selectSanitizedSearchResults, selectSelectedSearchResults, selectRemovedSearchResults,
  (agentInvites, sanitizedSearchResults, selectedSearchResults, removedSearchResults) => {
  const addedEmails = _.filter(_.map(agentInvites, invite => invite.email), email => !!email)
  const addedEmailsAndSelectedSearchResults = _.concat(addedEmails, selectedSearchResults, removedSearchResults)
  return _.filter(sanitizedSearchResults, item => !_.includes(addedEmailsAndSelectedSearchResults, item.email))
})

export const selectAgentInvitesListItems = createSelector(selectAgentInvites, selectSearchResults, (agentInvites, searchResults) => {
   const allInvites = _.concat(
    [],
    _.map(agentInvites, i => ({...i, added: true})),
    _.map(searchResults, i => ({...i, added: false})),
  )

  return _.reverse(_.sortBy(allInvites, ['key']))
})

export const selectHasAgentRecommendations = createSelector(selectAgentInvitesListItems, listItems => _.some(listItems, item => !!item.recommended))

export const selectSelectedInvites = createSelector(selectAgentInvites, invites => _.filter(invites, invite => invite.selected))

// TODO (Jared): Add the RDC metadata to the post
export const selectAgentInvitesToPost = createSelector(selectSelectedInvites, selectedInvites => _.map(selectedInvites, i => ({
  email: i.email,
  name: i.name,
  experience_level: i.experience_level,
  party_id: i.party_id,
  broker_recommendation_id: i.broker_recommendation_id,
  is_mvip_enabled: i.is_mvip_enabled,
  is_core_enabled: i.is_core_enabled,
  key: i.key,
})))

export const selectNonEmptyAgentInvites = createSelector(selectAgentInvites, invites => _.filter(invites, invite => invite.email))

export const selectNonEmptySelectedAgentInviteCount = createSelector(selectNonEmptyAgentInvites, nonEmptySelectedInvites =>
  _.filter(nonEmptySelectedInvites, invite => invite.selected).length)

export const selectTotalNonEmptyAgentInvites = createSelector(selectAgentInvitesListItems, invitesList =>
  _.filter(invitesList, invite => invite.email).length)

export const selectAgentFormErrors = createSelector(selectSelectedInvites, selectedInvites => _.some(selectedInvites, invite => !!invite.emailErrorMessage || !!invite.experienceLevelErrorMessage))

export const selectSelectedCount = createSelector(selectSelectedInvites, selectedInvites => selectedInvites.length)

//////////////////////////////////////
// ADMIN INVITES
//////////////////////////////////////

export const selectAdminInvites = createSelector(selectManageUsers, state => state.adminInvites || [])
export const selectNonEmptyAdminRows = createSelector(selectAdminInvites, invites =>  _.filter(invites, invite => !!invite.email || !!invite.name || !!invite.phone))
export const selectAdminFormErrors = createSelector(selectNonEmptyAdminRows, invites => _.some(invites, invite => !!invite.emailErrorMessage || !!invite.nameErrorMessage || !!invite.roleErrorMessage))
export const selectAdminInvitesCount = createSelector(selectNonEmptyAdminRows, invitesList => invitesList.length)
